import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import TenantService from '../services/tenant.service';
import type { Tier } from '@policy-tracker/types';

export const ENTITLEMENT_QUERY_KEYS = {
    all: ['entitlements'] as const,
    detail: (tenantId: string) => [...ENTITLEMENT_QUERY_KEYS.all, tenantId] as const,
};

/**
 * Hook to fetch entitlements (including tier) for a specific tenant.
 * Pass `enabled = false` when not in edit mode or when tenantId is not yet known.
 */
export function useEntitlements(tenantId: string | undefined, enabled: boolean) {
    return useQuery({
        queryKey: ENTITLEMENT_QUERY_KEYS.detail(tenantId ?? ''),
        queryFn: () => TenantService.getEntitlements(tenantId!),
        enabled: enabled && !!tenantId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

/**
 * Hook to change the billing tier for a tenant.
 * Invalidates the entitlements cache for the affected tenant on success.
 */
export function useChangeTier() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ tenantId, tier }: { tenantId: string; tier: Tier }) => TenantService.changeTier(tenantId, tier),
        onSuccess: (_: void, { tenantId }: { tenantId: string; tier: Tier }) => {
            queryClient.invalidateQueries({ queryKey: ENTITLEMENT_QUERY_KEYS.detail(tenantId) });
        },
    });
}
